import { useState, useContext, useEffect } from 'react';
import HeaderBackground from '../components/headerbackground.js';
import PasswordStrengthChecker from '../components/PasswordStrengthChecker.js';
import getAPI from '../functions/API/getAPI.js';
import postAPI from '../functions/API/postAPI.js';
import { toast } from '../lib/toast.js';
import GlobalContext from "../context/GlobalContext.js";
import { useNavigate } from 'react-router-dom';
import {
    Phone, Mail, ChevronRight, X, PhoneCall, AtSign, Plus,
    Lock, Trash2, Globe, HelpCircle, Smartphone,
} from 'lucide-react';

export default function Profile() {
    const navigate = useNavigate();
    const [loading, isLoading] = useState(true);
    const [contactdetails, setContactDetails] = useState([]);
    const [profile, setProfile] = useState(null);
    const { theme, themeCSS, settings } = useContext(GlobalContext);
    const primaryColor = settings?.primary_hex_colour || '#813468';

    const [methods, setMethods] = useState([]);
    const [currentEmail, setCurrentEmail] = useState('');

    // Modal state — only one open at a time
    const [activeModal, setActiveModal] = useState(null);
    // 'contacts' | 'preferred' | 'email' | 'newContact' | 'password' | 'delete'

    // Form states
    const [contactEdits, setContactEdits] = useState({});
    const [newContactMethod, setNewContactMethod] = useState({});
    const [newEmail, setNewEmail] = useState('');
    const [changeEmailPassword, setChangeEmailPassword] = useState('');
    const [changeEmail2FACode, setChangeEmail2FACode] = useState('');
    const [twoFAStep, setTwoFAStep] = useState(false);
    const [passwordChange, setPasswordChange] = useState({ currentpassword: '', password: '', password_confirmation: '' });
    const [passwordSecure, setPasswordSecure] = useState(false);
    const [deletePassword, setDeletePassword] = useState('');
    const [saving, setSaving] = useState(false);

    // ── API ───────────────────────────────────────────────────────────────────
    async function loadData() {
        const [profileRes, contactRes, methodsRes] = await Promise.all([
            getAPI('userdetails'),
            getAPI('contactdetail'),
            getAPI('getcontactmethodmappings'),
        ]);
        if (profileRes.response?.ok) {
            setProfile(profileRes.data);
            setCurrentEmail(profileRes.data.email);
        }
        if (contactRes.response?.ok) setContactDetails(Array.isArray(contactRes.data) ? contactRes.data : []);
        if (methodsRes.response?.ok) setMethods(Array.isArray(methodsRes.data) ? methodsRes.data : []);
        isLoading(false);
    }

    useEffect(() => { loadData(); }, []);

    function closeModal() {
        setActiveModal(null);
        setContactEdits({});
        setNewContactMethod({});
        setNewEmail('');
        setChangeEmailPassword('');
        setChangeEmail2FACode('');
        setTwoFAStep(false);
        setPasswordChange({ currentpassword: '', password: '', password_confirmation: '' });
        setDeletePassword('');
    }

    // ── Contact details ───────────────────────────────────────────────────────
    function openContactsModal() {
        const initial = {};
        contactdetails.forEach(cd => { initial[cd.id] = cd.reference || ''; });
        setContactEdits(initial);
        setActiveModal('contacts');
    }

    async function saveContactEdits() {
        setSaving(true);
        // Only send edits where the value actually changed from the original
        const edits = Object.entries(contactEdits).filter(([id, v]) => {
            const original = contactdetails.find(cd => String(cd.id) === String(id));
            return original && v !== (original.reference || '');
        });
        if (edits.length === 0) {
            setSaving(false);
            closeModal();
            return;
        }
        for (const [id, reference] of edits) {
            if (!reference.trim()) {
                toast.error('Contact details cannot be empty');
                setSaving(false);
                return;
            }
            await postAPI('contactdetail/update', { id: Number(id), reference, isPrimary: false });
        }
        toast.success('Contact details updated');
        await loadData();
        setSaving(false);
        closeModal();
    }

    // ── Preferred contact ─────────────────────────────────────────────────────
    async function setPrimary(cd) {
        // Optimistically update UI immediately
        setContactDetails(prev => prev.map(c => ({ ...c, isprimary: c.id === cd.id ? 1 : 0 })));
        const res = await postAPI('contactdetail/update', { id: cd.id, reference: cd.reference, isPrimary: true });
        if (res.response?.ok) {
            toast.success('Preferred contact method updated');
            await loadData();
        } else {
            toast.error('Something went wrong');
            await loadData(); // revert to server state
        }
    }

    // ── New contact method ────────────────────────────────────────────────────
    async function createContact() {
        if (!newContactMethod.methodid) { toast.error('Please select a contact method'); return; }
        if (!newContactMethod.reference) { toast.error('Please enter your contact information'); return; }
        setSaving(true);
        const res = await postAPI('createnewcontactdetail', { id: newContactMethod.methodid, reference: newContactMethod.reference });
        if (res.response?.ok) {
            toast.success('New contact method added');
            await loadData();
            closeModal();
        } else {
            toast.error('Enter a valid phone number or email address');
        }
        setSaving(false);
    }

    // ── Change email ──────────────────────────────────────────────────────────
    async function requestEmailCode() {
        setSaving(true);
        const res = await postAPI('getemailchangecode', { email: newEmail, password: changeEmailPassword });
        const sc = res.response?.status;
        if (sc === 200) { setTwoFAStep(true); }
        else if (sc === 400) toast.error('Given email is the same as current email');
        else if (sc === 403) toast.error('Invalid password');
        else if (sc === 422) toast.error('Please enter a valid email address and your current password');
        else toast.error('An error occurred, please try again');
        setSaving(false);
    }

    async function confirmEmailChange() {
        setSaving(true);
        const res = await postAPI('completeemailchange', { email: newEmail, password: changeEmailPassword, token: changeEmail2FACode });
        if (res.response?.status === 200) {
            setCurrentEmail(newEmail);
            toast.success('Email address updated successfully');
            closeModal();
        } else if (res.response?.status === 403) {
            toast.error('Incorrect verification code');
        } else {
            toast.error('An error occurred, please try again');
        }
        setSaving(false);
    }

    // ── Change password ───────────────────────────────────────────────────────
    async function changePassword() {
        if (!passwordSecure) { toast.error('Your password does not meet the minimum requirements'); return; }
        if (passwordChange.password !== passwordChange.password_confirmation) { toast.error('Your new passwords do not match'); return; }
        setSaving(true);
        const res = await postAPI('changepassword', passwordChange);
        if (res.response?.status === 200) {
            toast.success('Password changed, you will now be logged out');
            setTimeout(() => { localStorage.clear(); window.location.href = '/login'; }, 3000);
        } else {
            toast.error('Your current password is invalid');
        }
        setSaving(false);
    }

    // ── Delete account ────────────────────────────────────────────────────────
    async function deleteAccount() {
        setSaving(true);
        const res = await postAPI('deletetenantaccount', { password: deletePassword });
        if (res.response?.status === 200) {
            toast.success('Account deleted. You will now be logged out');
            setTimeout(() => { localStorage.clear(); window.location.href = '/login'; }, 3000);
        } else {
            toast.error('Your password is invalid');
        }
        setSaving(false);
    }

    // ── Helpers ───────────────────────────────────────────────────────────────
    function contactIcon(cd) {
        const d = (cd.description || '').toLowerCase();
        if (d.includes('mobile') || d.includes('phone')) return Smartphone;
        if (d.includes('email')) return Mail;
        return Phone;
    }

    function getInitials() {
        if (!profile) return '';
        return `${(profile.firstname || '')[0] || ''}${(profile.lastname || '')[0] || ''}`.toUpperCase();
    }

    const inputClass = `w-full rounded-xl px-4 py-3 text-sm border-0 ring-0 outline-none focus:outline-none focus:ring-0 ${
        theme === 'dark'
            ? 'bg-slate-600 text-white placeholder-gray-400'
            : theme === 'highContrast'
            ? 'bg-white text-black placeholder-gray-600 border-2 border-black'
            : 'bg-white text-gray-900 placeholder-gray-400'
    }`;

    // ── Menu row component ────────────────────────────────────────────────────
    function MenuRow({ Icon, label, subtitle, onClick, danger }) {
        return (
            <button
                onClick={onClick}
                className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors ${themeCSS.cardHover}`}
            >
                <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        danger ? 'bg-red-100' : (theme === 'dark' ? 'bg-slate-700' : 'bg-gray-100')
                    }`}
                >
                    <Icon size={20} strokeWidth={1.5} className={danger ? 'text-red-500' : themeCSS.cardMuted} />
                </div>
                <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${danger ? 'text-red-500' : themeCSS.cardTitle}`}>{label}</p>
                    {subtitle && <p className={`text-xs mt-0.5 ${themeCSS.cardMuted}`}>{subtitle}</p>}
                </div>
                <ChevronRight size={18} className={themeCSS.cardMuted} strokeWidth={1.5} />
            </button>
        );
    }

    function Divider() {
        return <div className={`mx-4 border-t ${theme === 'dark' ? 'border-slate-700' : 'border-gray-100'}`} />;
    }

    // ── Modal wrapper — plain function returning JSX, NOT a component ─────────
    function renderModalSheet(title, content) {
        return (
            <>
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={closeModal} />
                <div className="fixed inset-0 z-50 flex items-end lg:items-center lg:justify-center lg:p-4">
                    <div className={`w-full animate-slideUp flex flex-col rounded-t-2xl lg:rounded-2xl shadow-2xl lg:max-w-lg overflow-hidden ${
                        theme === 'dark' ? 'bg-slate-800' : theme === 'highContrast' ? 'bg-white' : 'bg-gray-50'
                    }`} style={{ maxHeight: '85vh' }}>
                        <div className="flex justify-center pt-3 pb-1 flex-shrink-0 lg:hidden">
                            <div className={`w-10 h-1 rounded-full ${theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'}`} />
                        </div>
                        <div className="flex items-center justify-between px-5 pb-3 lg:pt-5 flex-shrink-0">
                            <h2 className={`text-lg font-bold ${themeCSS.cardTitle}`}>{title}</h2>
                            <button onClick={closeModal} className="text-sm font-semibold lg:hidden" style={{ color: primaryColor }}>Done</button>
                            <button
                                onClick={closeModal}
                                className={`hidden lg:flex w-8 h-8 rounded-full items-center justify-center transition-colors ${theme === 'dark' ? 'hover:bg-slate-700' : 'hover:bg-gray-200'}`}
                            >
                                <X size={18} className={themeCSS.cardMuted} strokeWidth={2} />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto px-5 pb-8">
                            {content}
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // ── Skeleton ──────────────────────────────────────────────────────────────
    function Skeleton() {
        return (
            <div className="space-y-4">
                <div className={`${themeCSS.cardBg} rounded-2xl p-5 flex items-center gap-4`}>
                    <div className={`w-14 h-14 rounded-full ${themeCSS.skeleton} animate-pulse`} />
                    <div className="flex-1 space-y-2">
                        <div className={`h-5 w-32 rounded ${themeCSS.skeleton} animate-pulse`} />
                        <div className={`h-3 w-48 rounded ${themeCSS.skeleton} animate-pulse`} />
                    </div>
                </div>
                {[...Array(4)].map((_, i) => (
                    <div key={i} className={`${themeCSS.cardBg} rounded-2xl px-4 py-4 flex items-center gap-3`}>
                        <div className={`w-10 h-10 rounded-xl ${themeCSS.skeleton} animate-pulse`} />
                        <div className={`h-4 flex-1 rounded ${themeCSS.skeleton} animate-pulse`} />
                    </div>
                ))}
            </div>
        );
    }

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen" style={{ backgroundColor: themeCSS.pageBg }}>
            <HeaderBackground pageTitle="Settings" onBack={() => navigate(-1)} />

            <div className="max-w-2xl mx-auto px-4 py-4 pb-32">
                {loading ? <Skeleton /> : (
                    <div className="space-y-5">

                        {/* Profile card */}
                        <div className={`${themeCSS.cardBg} rounded-2xl p-5 flex items-center gap-4 shadow-sm`}>
                            <div
                                className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold text-white flex-shrink-0"
                                style={{ backgroundColor: primaryColor }}
                            >
                                {getInitials()}
                            </div>
                            <div className="min-w-0">
                                <p className={`text-base font-bold ${themeCSS.cardTitle}`}>
                                    {profile?.firstname} {profile?.lastname}
                                </p>
                                <p className={`text-sm ${themeCSS.cardMuted} truncate`}>{currentEmail}</p>
                            </div>
                        </div>

                        {/* ACCOUNT section */}
                        <div>
                            <p className={`text-xs font-bold uppercase tracking-wider px-1 mb-2 ${themeCSS.cardMuted}`}>Account</p>
                            <div className={`${themeCSS.cardBg} rounded-2xl shadow-sm overflow-hidden`}>
                                <MenuRow Icon={PhoneCall} label="Change your contact details" onClick={openContactsModal} />
                                <Divider />
                                <MenuRow Icon={Phone} label="Set your preferred contact method" onClick={() => setActiveModal('preferred')} />
                                <Divider />
                                <MenuRow Icon={AtSign} label="Change your login email address" subtitle={currentEmail} onClick={() => setActiveModal('email')} />
                                <Divider />
                                <MenuRow Icon={Plus} label="Add new contact details" onClick={() => setActiveModal('newContact')} />
                                <Divider />
                                <MenuRow Icon={Lock} label="Change your password" onClick={() => setActiveModal('password')} />
                            </div>
                        </div>

                        {/* RESOURCES section */}
                        {settings?.website_url && (
                            <div>
                                <p className={`text-xs font-bold uppercase tracking-wider px-1 mb-2 ${themeCSS.cardMuted}`}>Resources</p>
                                <div className={`${themeCSS.cardBg} rounded-2xl shadow-sm overflow-hidden`}>
                                    <MenuRow Icon={Globe} label="Our website" onClick={() => window.open(settings.website_url, '_blank')} />
                                    {settings?.support_url && (
                                        <>
                                            <Divider />
                                            <MenuRow Icon={HelpCircle} label="Need help?" onClick={() => window.open(settings.support_url, '_blank')} />
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* DANGER ZONE */}
                        <div>
                            <p className={`text-xs font-bold uppercase tracking-wider px-1 mb-2 ${themeCSS.cardMuted}`}>Danger Zone</p>
                            <div className={`${themeCSS.cardBg} rounded-2xl shadow-sm overflow-hidden`}>
                                <MenuRow Icon={Trash2} label="Delete your account" subtitle="This action is irreversible" onClick={() => setActiveModal('delete')} danger />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ── MODALS ──────────────────────────────────────────────────────────── */}

            {/* Contact Details */}
            {activeModal === 'contacts' && renderModalSheet('Contact Details',
                <>
                    <p className={`text-sm mb-4 ${themeCSS.cardMuted}`}>
                        Keep your contact details up-to-date so we can reach you when needed.
                    </p>
                    <div className="space-y-3">
                        {contactdetails.map((cd) => {
                            const Icon = contactIcon(cd);
                            return (
                                <div key={cd.id} className={`${themeCSS.cardBg} rounded-xl overflow-hidden shadow-sm`}>
                                    <label className={`flex items-center gap-2 px-4 pt-3 text-xs font-semibold uppercase tracking-wider ${themeCSS.cardMuted}`}>
                                        <Icon size={14} strokeWidth={1.5} />
                                        {cd.description}
                                    </label>
                                    <input
                                        type="text"
                                        value={contactEdits[cd.id] ?? cd.reference ?? ''}
                                        onChange={(e) => setContactEdits(prev => ({ ...prev, [cd.id]: e.target.value }))}
                                        className={inputClass}
                                    />
                                </div>
                            );
                        })}
                    </div>
                    <button
                        onClick={saveContactEdits}
                        disabled={saving}
                        className="w-full py-3.5 rounded-xl text-sm font-semibold text-white mt-4 transition-opacity disabled:opacity-60"
                        style={{ backgroundColor: primaryColor }}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </>
            )}

            {/* Preferred Contact Method */}
            {activeModal === 'preferred' && renderModalSheet('Preferred Contact',
                <>
                    <p className={`text-sm mb-4 ${themeCSS.cardMuted}`}>
                        Select your preferred primary contact method. We will try and contact you via this method whenever possible.
                    </p>
                    <div className="space-y-2">
                        {contactdetails.map(cd => {
                            const Icon = contactIcon(cd);
                            const selected = !!cd.isprimary;
                            return (
                                <button
                                    key={cd.id}
                                    onClick={() => !selected && setPrimary(cd)}
                                    disabled={saving}
                                    className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left transition-colors shadow-sm ${themeCSS.cardBg} ${!selected ? themeCSS.cardHover : ''}`}
                                    style={selected ? { outline: `2px solid ${primaryColor}`, outlineOffset: '-2px', backgroundColor: `${primaryColor}0d` } : {}}
                                >
                                    <div
                                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                                        style={{ backgroundColor: selected ? `${primaryColor}22` : (theme === 'dark' ? '#1e293b' : '#F3F4F6') }}
                                    >
                                        <Icon size={20} strokeWidth={1.5} style={{ color: selected ? primaryColor : (theme === 'dark' ? '#94A3B8' : '#6B7280') }} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm font-medium ${themeCSS.cardTitle}`}>{cd.description}</p>
                                        <p className={`text-xs mt-0.5 ${themeCSS.cardMuted}`}>{cd.reference}</p>
                                    </div>
                                    {selected && (
                                        <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: primaryColor }}>
                                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                                <path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </>
            )}

            {/* Change Email */}
            {activeModal === 'email' && renderModalSheet('Change Email',
                !twoFAStep ? (
                    <>
                        <p className={`text-sm mb-1 ${themeCSS.cardMuted}`}>
                            Your account email address is not linked to your contact preferences.
                        </p>
                        <p className={`text-sm mb-4 ${themeCSS.cardTitle}`}>
                            Current email: <strong>{currentEmail}</strong>
                        </p>
                        <div className="space-y-3">
                            <div className={`${themeCSS.cardBg} rounded-xl overflow-hidden shadow-sm`}>
                                <label className={`block px-4 pt-3 text-xs font-semibold uppercase tracking-wider ${themeCSS.cardMuted}`}>New Email</label>
                                <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="Enter your new email address" className={inputClass} />
                            </div>
                            <div className={`${themeCSS.cardBg} rounded-xl overflow-hidden shadow-sm`}>
                                <label className={`block px-4 pt-3 text-xs font-semibold uppercase tracking-wider ${themeCSS.cardMuted}`}>Password</label>
                                <input type="password" value={changeEmailPassword} onChange={e => setChangeEmailPassword(e.target.value)} placeholder="Enter your current password" className={inputClass} />
                            </div>
                        </div>
                        <button
                            onClick={requestEmailCode}
                            disabled={saving || !newEmail || !changeEmailPassword}
                            className="w-full py-3.5 rounded-xl text-sm font-semibold text-white mt-4 transition-opacity disabled:opacity-40"
                            style={{ backgroundColor: primaryColor }}
                        >
                            {saving ? 'Sending...' : 'Change account email'}
                        </button>
                    </>
                ) : (
                    <>
                        <p className={`text-sm mb-4 ${themeCSS.cardMuted}`}>
                            We've sent a verification code to <strong>{newEmail}</strong>. Please enter it below.
                        </p>
                        <div className={`${themeCSS.cardBg} rounded-xl overflow-hidden shadow-sm`}>
                            <label className={`block px-4 pt-3 text-xs font-semibold uppercase tracking-wider ${themeCSS.cardMuted}`}>Verification Code</label>
                            <input
                                type="text"
                                value={changeEmail2FACode}
                                onChange={e => setChangeEmail2FACode(e.target.value)}
                                placeholder="Enter your code"
                                className={`${inputClass} text-center text-lg tracking-widest`}
                            />
                        </div>
                        <button
                            onClick={confirmEmailChange}
                            disabled={saving || !changeEmail2FACode}
                            className="w-full py-3.5 rounded-xl text-sm font-semibold text-white mt-4 transition-opacity disabled:opacity-40"
                            style={{ backgroundColor: primaryColor }}
                        >
                            {saving ? 'Verifying...' : 'Confirm'}
                        </button>
                        <button onClick={() => setTwoFAStep(false)} className={`w-full py-3 rounded-xl text-sm font-semibold mt-2 ${themeCSS.cardMuted}`}>
                            Back
                        </button>
                    </>
                )
            )}

            {/* Add New Contact */}
            {activeModal === 'newContact' && renderModalSheet('Add Contact Method',
                <>
                    <p className={`text-sm mb-4 ${themeCSS.cardMuted}`}>
                        Add a new way for us to contact you.
                    </p>
                    <div className="space-y-3">
                        <div className={`${themeCSS.cardBg} rounded-xl overflow-hidden shadow-sm`}>
                            <label className={`block px-4 pt-3 text-xs font-semibold uppercase tracking-wider ${themeCSS.cardMuted}`}>Method Type</label>
                            <select
                                value={newContactMethod.methodid || ''}
                                onChange={e => setNewContactMethod(prev => ({ ...prev, methodid: e.target.value }))}
                                className={inputClass}
                            >
                                <option value="">Select a method type</option>
                                {methods.map(m => (
                                    <option key={m.id} value={m.id}>{m.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className={`${themeCSS.cardBg} rounded-xl overflow-hidden shadow-sm`}>
                            <label className={`block px-4 pt-3 text-xs font-semibold uppercase tracking-wider ${themeCSS.cardMuted}`}>Contact Information</label>
                            <input
                                type="text"
                                value={newContactMethod.reference || ''}
                                onChange={e => setNewContactMethod(prev => ({ ...prev, reference: e.target.value }))}
                                placeholder="Enter your contact information"
                                className={inputClass}
                            />
                        </div>
                    </div>
                    <button
                        onClick={createContact}
                        disabled={saving}
                        className="w-full py-3.5 rounded-xl text-sm font-semibold text-white mt-4 transition-opacity disabled:opacity-60"
                        style={{ backgroundColor: primaryColor }}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </>
            )}

            {/* Change Password */}
            {activeModal === 'password' && renderModalSheet('Change Password',
                <>
                    <div className="space-y-3">
                        <div className={`${themeCSS.cardBg} rounded-xl overflow-hidden shadow-sm`}>
                            <label className={`block px-4 pt-3 text-xs font-semibold uppercase tracking-wider ${themeCSS.cardMuted}`}>Current Password</label>
                            <input type="password" value={passwordChange.currentpassword} onChange={e => setPasswordChange(prev => ({ ...prev, currentpassword: e.target.value }))} placeholder="Enter your current password" className={inputClass} />
                        </div>
                        <div className={`${themeCSS.cardBg} rounded-xl overflow-hidden shadow-sm`}>
                            <label className={`block px-4 pt-3 text-xs font-semibold uppercase tracking-wider ${themeCSS.cardMuted}`}>New Password</label>
                            <input type="password" value={passwordChange.password} onChange={e => setPasswordChange(prev => ({ ...prev, password: e.target.value }))} placeholder="Enter your new password" className={inputClass} />
                        </div>
                        <PasswordStrengthChecker password={passwordChange.password} passwordSecure={setPasswordSecure} />
                        <div className={`${themeCSS.cardBg} rounded-xl overflow-hidden shadow-sm`}>
                            <label className={`block px-4 pt-3 text-xs font-semibold uppercase tracking-wider ${themeCSS.cardMuted}`}>Confirm New Password</label>
                            <input type="password" value={passwordChange.password_confirmation} onChange={e => setPasswordChange(prev => ({ ...prev, password_confirmation: e.target.value }))} placeholder="Re-enter your new password" className={inputClass} />
                        </div>
                    </div>
                    <button
                        onClick={changePassword}
                        disabled={saving || !passwordSecure}
                        className="w-full py-3.5 rounded-xl text-sm font-semibold text-white mt-4 transition-opacity disabled:opacity-40"
                        style={{ backgroundColor: primaryColor }}
                    >
                        {saving ? 'Changing...' : 'Change Password'}
                    </button>
                </>
            )}

            {/* Delete Account */}
            {activeModal === 'delete' && renderModalSheet('Delete Account',
                <>
                    <div className="rounded-xl px-4 py-3 flex gap-3 mb-4" style={{ backgroundColor: '#FEF2F2' }}>
                        <Trash2 size={16} className="flex-shrink-0 mt-0.5 text-red-500" strokeWidth={1.5} />
                        <p className="text-xs text-red-700">
                            Deleting your account is <strong>irreversible</strong>. You can re-register at any time.
                        </p>
                    </div>
                    <p className={`text-sm mb-4 ${themeCSS.cardMuted}`}>
                        If you no longer wish to manage your account via our portal, enter your password below to confirm deletion.
                    </p>
                    <div className={`${themeCSS.cardBg} rounded-xl overflow-hidden shadow-sm`}>
                        <label className={`block px-4 pt-3 text-xs font-semibold uppercase tracking-wider ${themeCSS.cardMuted}`}>Password</label>
                        <input type="password" value={deletePassword} onChange={e => setDeletePassword(e.target.value)} placeholder="Enter your current password" className={inputClass} />
                    </div>
                    <button
                        onClick={deleteAccount}
                        disabled={saving || !deletePassword}
                        className="w-full py-3.5 rounded-xl text-sm font-semibold text-white mt-4 transition-opacity disabled:opacity-40 bg-red-600"
                    >
                        {saving ? 'Deleting...' : 'Delete Account'}
                    </button>
                </>
            )}
        </div>
    );
}
